import { useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Loader2, Trash2 } from 'lucide-react';
import { PopMenu } from './PopMenu';
import { byName, type MaterialsAnswer } from './materialsRun';
import { cssRgb } from './sampleColour';
import { materialText } from './variantLabel';
import { clock } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

// EVERY READING OF THIS PRODUCT, NEWEST FIRST, AND WHICH ONE THE MESHES WEAR — as a dropdown.
// The same chair read twice gives two answers and the interesting question is which is right, so
// a reading is kept rather than replaced; choosing one to look at is this menu, and putting it on
// the meshes is the Apply under the table.
//
// A MENU AND NOT A LIST: six readings stacked above the table pushed it off the screen, and the
// one that matters is the one being looked at. That one is the closed menu's face.
export function ReadingsMenu({ reads, shownId, appliedId, calling, elapsed, busyId, onSelect, canDelete, onDelete }: {
  reads: MaterialsAnswer[];
  // The one on screen, and the one the meshes wear.
  shownId: string;
  appliedId?: string;
  // A press is out: its row is the face, with its clock, until it lands.
  calling: boolean;
  elapsed: number;
  // The row with a spinner in place of its tick — being measured, or being applied.
  busyId?: string;
  onSelect: (id: string) => void;
  canDelete: (id: string) => boolean;
  onDelete: (id: string) => void;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const shown = reads.find((one) => one.id === shownId);
  const chevron = (
    <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
  );
  // AS WIDE AS THE FACE it drops from, so the rows line up with it.
  const [width, setWidth] = useState(0);
  const toggle = () => {
    setWidth(anchor.current?.offsetWidth ?? 0);
    setOpen((was) => !was);
  };
  if (!shown && !calling) return null;
  return (
    <div ref={anchor} className="flex w-full max-w-3xl justify-center">
      {calling || !shown ? (
        // The face while a press is out; it still opens the list.
        <button type="button" onClick={toggle} className="w-full">
          <PendingRow elapsed={elapsed} trailing={chevron} />
        </button>
      ) : (
        <ReadRow
          read={shown}
          on
          applied={shown.id === appliedId}
          busy={shown.id === busyId}
          onSelect={toggle}
          trailing={chevron}
        />
      )}
      {open && (
        <PopMenu anchor={anchor} onClose={() => setOpen(false)}>
          <div className="flex flex-col gap-1.5 px-1.5" style={{ width: width || undefined }}>
            {calling && <PendingRow elapsed={elapsed} />}
            {reads.map((one) => (
              <ReadRow
                key={one.id}
                read={one}
                on={!calling && one.id === shownId}
                applied={one.id === appliedId}
                busy={one.id === busyId}
                onSelect={() => {
                  onSelect(one.id);
                  setOpen(false);
                }}
                onDelete={canDelete(one.id) ? () => { setOpen(false); onDelete(one.id); } : undefined}
              />
            ))}
          </div>
        </PopMenu>
      )}
    </div>
  );
}

// THE READING BEING MADE, as a row of the list it will join — drawn as the one looked at, with a
// spinner and its clock where a reading has its date.
function PendingRow({ elapsed, trailing }: { elapsed: number; trailing?: ReactNode }) {
  const { t } = useLanguage();
  return (
    <div className="flex w-full max-w-3xl items-center gap-3 rounded-lg border border-blue-500 bg-white px-3 py-1.5 text-xs text-blue-700 shadow-sm">
      {/* CENTRED, the clock beside the words it times; the chevron stays at the edge. */}
      <span className="flex flex-1 items-center justify-center gap-2">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        <span>{t('notice.3d.matReading')}</span>
        <span className="tabular-nums opacity-60">({clock(elapsed)})</span>
      </span>
      {trailing}
    </div>
  );
}

// ONE READING IN THE HISTORY: the row that opens it, and the tick that says the meshes wear it.
//
// TWO STATES, AND THEY ARE DIFFERENT QUESTIONS. `on` is the one being looked at — free, local,
// and how two readings of one chair get compared. `applied` is a claim about bytes in ten
// files, and changing that is a button under the numbers, not a click on a list.
//
// WHAT A READING LOOKS LIKE AT A GLANCE: when it was read, what read it, and its colours as
// swatches. Those are what differ between two readings of one chair, and the whole reason to
// keep both.
function ReadRow({ read, on, applied, busy, onSelect, onDelete, trailing }: {
  read: MaterialsAnswer;
  on: boolean;
  applied: boolean;
  busy: boolean;
  onSelect: () => void;
  // Absent where the reading may not go — see the caller.
  onDelete?: () => void;
  // In the bin's place: the menu's chevron, on the row that opens it.
  trailing?: ReactNode;
}) {
  const { t } = useLanguage();
  const when = read.at
    ? new Date(read.at).toLocaleString(undefined, {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    })
    : '—';
  return (
    // THE ROW IS A SURFACE HOLDING TWO BUTTONS — open it, or bin it — since a button cannot
    // hold another.
    <div
      // A SURFACE AND NOT AN OUTLINE. These sit directly on the blueprint page, where every
      // other control — the run button, the model's name, the switcher's own cards right
      // above them — is an opaque card. Drawn as a border on nothing, a row read as a field
      // waiting to be filled in rather than as a thing to press.
      //
      // The same pair SectionNav uses, for the same reason: it is the component immediately
      // above this list and the two are one gesture.
      className={`flex w-full max-w-3xl items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-left text-xs shadow-sm transition-colors ${
        on
          ? 'border-blue-500 text-blue-700'
          : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-100'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={on ? 'true' : undefined}
        title={t(applied ? 'notice.3d.matIsApplied' : 'notice.3d.matShow')}
        className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center"
      >
      {/* THE TICK IS THE APPLIED ONE, wherever the reader happens to be looking — which is
          the one thing this list has to keep saying while somebody walks it comparing rows. */}
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {busy
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : applied
            ? <Check className="h-3.5 w-3.5 text-blue-500" />
            : null}
      </span>
      <span className="flex flex-wrap items-center gap-2">
        {byName(read.materials).map((m) => (
          <span key={m.name} className="inline-flex items-center gap-1">
            <span
              className="h-3 w-3 shrink-0 rounded border border-slate-400"
              style={{ background: cssRgb(m.rgb) }}
            />
            <span className={on ? '' : 'text-slate-900'}>{m.name}</span>
          </span>
        ))}
        {/* AND THE HARDWARE'S, after a rule: the same press answered both. */}
        {read.hardware && read.hardware.materials.length > 0 && (
          <span className="h-3 w-px bg-slate-300" />
        )}
        {read.hardware?.materials.map((m) => (
          <span key={materialText(m.pair)} className="inline-flex items-center gap-1">
            <span
              className="h-3 w-3 shrink-0 rounded border border-slate-400"
              style={{ background: cssRgb(m.colour ?? m.rgb) }}
            />
            <span className={on ? '' : 'text-slate-900'}>{materialText(m.pair)}</span>
          </span>
        ))}
      </span>
      {/* WHEN IT WAS READ, after its colours, in brackets: it is what tells two readings apart,
          so it carries the row's own ink. */}
      <span className={`tabular-nums ${on ? '' : 'text-slate-900'}`}>
        ({when})
      </span>
      </button>
      {/* The bin's place is kept where there is none, so the counts still line up down the list. */}
      <span className="flex h-6 w-6 shrink-0 items-center justify-center">
        {/* THE CHEVRON OPENS THE MENU TOO: it is what a reader reaches for. */}
        {trailing && (
          <button type="button" onClick={onSelect} className="flex h-6 w-6 items-center justify-center">
            {trailing}
          </button>
        )}
        {!trailing && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            title={t('notice.3d.matDelete')}
            aria-label={t('notice.3d.matDelete')}
            className="rounded p-1 text-slate-400 transition-colors hover:text-rose-500"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
